//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025, 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { chipColorForStatus } from './UnifiedCardData';
import { UnifiedCardData, formatDate, displayOrNA } from './UnifiedCardData';
import DefectIcon from '../../../icons/DefectIcon.svg';
import StoryIcon from '../../../icons/StoryIcon.svg';
import EpicIcon from '../../../icons/EpicIcon.svg';
import RelatedLinks from './RelatedLinks';

export const IssueCard: React.FC<{
  card: UnifiedCardData;
  iconSrc: string;
  iconBg: string;
  typeLabel: string;
}> = ({ card, iconSrc, iconBg, typeLabel }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className="mb-6 rounded-lg bg-[#f2f4f5] p-6 dark:bg-[#393939]"
      id="id-issue-card-container"
      data-testid="issue-card-container"
    >
      <div
        className="mb-4 flex items-start gap-3"
        id="id-issue-card-content"
        data-testid="issue-card-content"
      >
        <div
          className="mt-0.5 shrink-0"
          id="id-issue-card-icon-wrapper"
          data-testid="issue-card-icon-wrapper"
        >
          <div
            className="flex size-8 items-center justify-center rounded"
            id="id-issue-card-icon"
            data-testid="issue-card-icon"
          >
            {/* Primary icon supplied by parent */}
            <img
              src={iconSrc}
              alt={`${typeLabel} icon`}
              className="size-8 object-contain"
            />
          </div>
        </div>
        <div
          className="min-w-0 flex-1"
          id="id-issue-card-details"
          data-testid="issue-card-details"
        >
          <div
            className="mb-1 flex items-center gap-2"
            id="id-issue-card-title-row"
            data-testid="issue-card-title-row"
          >
            <span
              className="text-[12px] text-gray-500 dark:text-gray-400"
              id="id-issue-card-type-label"
              data-testid="issue-card-type-label"
            >
              {typeLabel}
              {/* Inline issue type icon if applicable */}
              {card.type === 'Issues' &&
                card.status &&
                (() => {
                  const lower = card.status.toLowerCase();
                  let icon: string | null = null;
                  if (lower === 'story') icon = StoryIcon;
                  else if (lower === 'defect') icon = DefectIcon;
                  else if (lower === 'epic') icon = EpicIcon;
                  return icon ? (
                    <img
                      src={icon}
                      alt={`${card.status} icon`}
                      className="ml-1.5 inline size-4 align-text-bottom"
                    />
                  ) : null;
                })()}
            </span>
            <span
              className="text-[12px] text-gray-300 dark:text-gray-600"
              id="id-issue-card-title-separator"
              data-testid="issue-card-title-separator"
            >
              |
            </span>
            <span
              className="cursor-default text-[14px] font-medium text-[#0043ce] dark:text-brand-interactiveDark"
              id="id-issue-card-title"
              data-testid="issue-card-title"
            >
              {card.cardTitle}
            </span>
          </div>
          <div
            className="mt-1 text-[12px] text-gray-600 dark:text-gray-300"
            id="id-issue-card-metadata"
            data-testid="issue-card-metadata"
          >
            <span
              id="id-issue-card-created-label"
              data-testid="issue-card-created-label"
            >
              {t('created')}:
            </span>{' '}
            <span
              id="id-issue-card-created-value"
              data-testid="issue-card-created-value"
            >
              {(() => {
                const formatted = formatDate(card.created);
                return formatted === '--' ? t('not-available') : formatted;
              })()}
            </span>
            <span
              className="mx-1"
              id="id-issue-card-separator-1"
              data-testid="issue-card-separator-1"
            >
              |
            </span>
            <span
              id="id-issue-card-owner-label"
              data-testid="issue-card-owner-label"
            >
              {t('owner')}:
            </span>
            <span
              className="ml-1"
              id="id-issue-card-owner-value"
              data-testid="issue-card-owner-value"
            >
              {displayOrNA(card.owner, t)}
            </span>
            <span
              className="mx-1"
              id="id-issue-card-separator-2"
              data-testid="issue-card-separator-2"
            >
              |
            </span>
            <span
              id="id-issue-card-loop-label"
              data-testid="issue-card-loop-label"
            >
              {t('loop')}:
            </span>
            <span
              className="ml-1"
              id="id-issue-card-loop-value"
              data-testid="issue-card-loop-value"
            >
              {displayOrNA(card.loopName, t)}
            </span>
            {card.status && (
              <>
                <span
                  className="mx-1"
                  id="id-issue-card-separator-3"
                  data-testid="issue-card-separator-3"
                >
                  |
                </span>
                <span
                  id="id-issue-card-status-label"
                  data-testid="issue-card-status-label"
                >
                  {t('status')}:
                </span>
                <span
                  className={`ml-1 inline-flex h-[18px] items-center rounded-full px-2 text-[11px] font-medium ${chipColorForStatus(card.status)}`}
                  id="id-issue-card-status-value"
                  data-testid="issue-card-status-value"
                >
                  {card.status}
                </span>
              </>
            )}
          </div>
          {card.description && (
            <p className="mt-3 text-[12px] leading-5 text-gray-700 dark:text-gray-200">
              {displayOrNA(card.description, t)}
            </p>
          )}

          {/* Links Related - Accordion */}
          <div
            className="mt-4 border-t border-gray-300 pt-4 dark:border-gray-600"
            id="id-issue-card-links-section"
            data-testid="issue-card-links-section"
          >
            <button
              onClick={() => setExpanded((prev) => !prev)}
              className="flex items-center gap-2 text-[12px] text-[#0043ce] transition-colors hover:underline dark:text-brand-interactiveDark"
            >
              <span
                id="id-issue-card-links-label"
                data-testid="issue-card-links-label"
              >
                {t('links-related-result')}
              </span>
              <ChevronDown
                size={16}
                className={`transition-transform ${expanded ? 'rotate-180' : ''}`}
              />
            </button>
            {expanded && <RelatedLinks card={card} primaryLabel="issue-url" />}
          </div>
        </div>
      </div>
    </div>
  );
};
